import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { OfferStatus } from "../../../helpers/bidderHelper";
import { getPositionByStep, Step } from "../../../helpers/stepHelper";
import CrossIcon from "../../../assets/icons/cross";
import BurgerMenu from "../../../assets/icons/burger_menu";
import { useHeaderContext } from "../../../context/HeaderContext";
import { useFooterContext } from "../../../context/FooterContext";
import StatusBadge from "../../../components/ui/StatusBadge";
import ApprovalBadge from "../../../components/ui/ApprovalBadge";

export default function SidebarNav({ bidders, status, commentsTotal, canAccessParams, hasMissingRequiredFields = false }) {
  const { headerHeight } = useHeaderContext();
  const { footerHeight } = useFooterContext();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1024);
  const [isOpen, setIsOpen] = useState(isSmallScreen ? false : true);
  const navbarRef = useRef(null);

  const toggle = () => setIsOpen(!isOpen);
  const hide = () => setIsOpen(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1024);
      setIsOpen(window.innerWidth >= 1024);
    };

    const handleClickOutside = (event) => {
      if (isSmallScreen && navbarRef.current && !navbarRef.current.contains(event.target)) {
        hide();
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSmallScreen]);

  const sortedBidders = [...(bidders || [])].sort((a, b) => {
    const an = (a?.name || "").toString();
    const bn = (b?.name || "").toString();
    return an.localeCompare(bn, "fr", { sensitivity: "base" });
  });

  const isFieldsComplete = (enterprise) => {
    return (
      enterprise.enterpriseNumber
      && enterprise.name
      && enterprise.legalForm
      && enterprise.rawStreet
      && enterprise.postalCode
      && enterprise.locality
      && enterprise.country
      && enterprise.contacts.length > 0
      && enterprise.contacts.every(contact => contact.fullname && contact.civility && contact.job && contact.email)
    );
  };

  function getClassName(isActive) {
    return `flex items-center py-2 px-5 font-bold text-xl border-r-2 ${isActive ? "bg-blue-50 border-blue-300" : "hover:bg-gray-100 border-gray-50"}`;
  }

  function buildDotTitle(statusValue) {
    if (statusValue === OfferStatus.SELECTED) return "Lauréat";
    if (statusValue === OfferStatus.REJECTED) return "Offre rejetée";
    if (statusValue === OfferStatus.ANSWERED) return "Offre reçue (en cours)";
    return "Aucune offre encodée";
  }

  return (
    <>
      <aside
        ref={navbarRef}
        style={{ paddingTop: `${headerHeight}px`, paddingBottom: `${footerHeight}px` }}
        className={`fixed h-full bottom-0 left-0 z-30 w-72 transition-transform duration-300 ease-in-out transform ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="h-full w-full py-4 overflow-y-auto space-y-4 bg-gray-50">
          {isSmallScreen && (
            <button className="w-10 h-auto mx-4" onClick={toggle}>
              <span>
                <CrossIcon />
              </span>
            </button>
          )}

          <ul>
            <li>
              <NavLink
                to="launch"
                onClick={isSmallScreen ? hide : undefined}
                className={({ isActive }) => getClassName(isActive)}
              >
                <div className="flex items-center justify-between w-full">
                  <span>Note de lancement</span>
                  {hasMissingRequiredFields && (status === Step.WRITING_SPECIFICATIONS) && (
                    <span
                      className="text-red-600"
                      title="Champs obligatoires manquants"
                      aria-label="Champs obligatoires manquants"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-5 h-5"
                      >
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.72-1.36 3.485 0l6.518 11.594c.75 1.334-.214 2.99-1.742 2.99H3.48c-1.528 0-2.492-1.656-1.742-2.99L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-.25-6.75a.75.75 0 00-1.5 0v4a.75.75 0 001.5 0v-4z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </div>
              </NavLink>
            </li>
          </ul>

          {getPositionByStep[status] >= 7 && (
            <ul className="pt-4 border-t border-gray-200">
              <li>
                <NavLink
                  to="attribution"
                  onClick={isSmallScreen ? hide : undefined}
                  className={({ isActive }) => getClassName(isActive)}
                >
                  <span>Attribution & Notif</span>
                </NavLink>
              </li>
            </ul>
          )}

          <ul className="pt-4 border-t border-gray-200">
            <li>
              <NavLink
                to="comments"
                onClick={isSmallScreen ? hide : undefined}
                className={({ isActive }) => getClassName(isActive)}
              >
                <div className="flex justify-between items-center w-full">
                  <span>Commentaires</span>
                  {commentsTotal > 0 && (
                    <div className="bg-gray-200 rounded-full w-6 text-center font-medium text-base">
                      {commentsTotal}
                    </div>
                  )}
                </div>
              </NavLink>
            </li>
          </ul>

          {status === Step.END && (
            <ul className="pt-4 border-t border-gray-200">
              <li>
                <NavLink
                  to="tracking"
                  onClick={isSmallScreen ? hide : undefined}
                  className={({ isActive }) => getClassName(isActive)}
                >
                  <span>Suivi</span>
                </NavLink>
              </li>
            </ul>
          )}

          <ul className="pt-4 space-y-2 border-t border-gray-200">
            <li>
              <NavLink
                to="bidder"
                onClick={(e) => {
                  e.preventDefault();
                  if (isSmallScreen) hide();
                }}
                className={({ isActive }) => `${getClassName(isActive)} cursor-default`}
              >
                Soumissionnaires
              </NavLink>
            </li>

            {sortedBidders.map((bidder) => {
              const statusValue = bidder.offers?.[0]?.status;

              return (
                <li key={bidder.uuid}>
                  <NavLink
                    to={`bidder/${bidder.uuid}`}
                    end={false}
                    onClick={() => isSmallScreen && hide()}
                    className={({ isActive }) => "flex items-center gap-3 py-2 px-7 " + (isActive ? "bg-blue-50 border-r-2 border-blue-300" : "hover:bg-gray-100")}
                  >
                    <ApprovalBadge
                      status={statusValue}
                      title={buildDotTitle(statusValue)}
                      size="md"
                    />

                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="truncate">{bidder.name}</span>

                      {statusValue === OfferStatus.SELECTED && (
                        <span
                          className="relative -top-0.5 text-yellow-500"
                          title="Lauréat"
                          aria-label="Lauréat"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="w-5 h-5"
                            fill="currentColor"
                          >
                            <path d="M4 19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7.382l-3.553 2.13a1 1 0 0 1-1.355-.33L12 6.618l-3.092 5.8a1 1 0 0 1-1.355.33L4 11.618V19zm-1 2a1 1 0 1 0 0 2h18a1 1 0 1 0 0-2H3z" />
                          </svg>
                        </span>
                      )}
                    </div>

                    {!isFieldsComplete(bidder) && (
                      <div className="ml-auto">
                        <StatusBadge size="small" text="!" color="red" />
                      </div>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {canAccessParams && (
            <ul className="pt-4 border-t border-gray-200">
              <li>
                <NavLink
                  to="settings"
                  onClick={isSmallScreen ? hide : undefined}
                  className={({ isActive }) => getClassName(isActive)}
                >
                  <span>Paramètres</span>
                </NavLink>
              </li>
            </ul>
          )}
        </div>
      </aside>

      {isSmallScreen && (
        <div className="fixed">
          <button className="w-10 h-auto m-4 p-1 bg-gray-300 rounded-lg" onClick={toggle}>
            <span>
              <BurgerMenu />
            </span>
          </button>
        </div>
      )}
    </>
  );
}
